import React from 'react';
import copy from 'copy-to-clipboard';

import { calculatePasswordStrength } from '../../lib/calculatePasswordStrength';
import { generatePassword } from '../../lib/generatePassword';

import '../../styles/_beautiful-checkbox.scss';
import '../../styles/_beautiful-range.scss';
import scss from './PasswordGenerator.module.scss';

const MIN_PASSWORD_LENGTH = 6;
const MAX_PASSWORD_LENGTH = 32;
const STARTING_PASSWORD_LENGTH = 6;
const STARTING_PASSWORD_OPTIONS = ['lowercase', 'uppercase'];

export const PasswordGenerator: React.FunctionComponent = () => {
  const btnCopyRef = React.useRef<HTMLButtonElement | null>(null);
  const passwordLengthRef = React.useRef<HTMLInputElement | null>(null);

  // Local states.
  const [password, setPassword] = React.useState<string>('');
  const [passwordLength, setPasswordLength] = React.useState<number>(
    STARTING_PASSWORD_LENGTH,
  );
  const [passwordOptions, setPasswordOptions] = React.useState<Array<string>>(
    STARTING_PASSWORD_OPTIONS,
  );

  // Methods.
  const copyPasswordToClipboard = () => {
    if (!btnCopyRef.current) return;

    copy(password);

    btnCopyRef.current.innerText = 'Copied to clipboard!';

    setTimeout(() => {
      if (!btnCopyRef.current) return;

      btnCopyRef.current.innerText = 'Copy Password';
    }, 2000);
  };

  const regeneratePassword = () => {
    setPassword(generatePassword(passwordLength, passwordOptions));
  };

  // Events.
  const onPasswordLengthChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(evt.target.value);

    if (isNaN(value) || value < MIN_PASSWORD_LENGTH) {
      if (evt.type === 'blur') {
        evt.target.value = MIN_PASSWORD_LENGTH.toString();

        setPasswordLength(MIN_PASSWORD_LENGTH);

        return;
      }
    } else if (value > MAX_PASSWORD_LENGTH) {
      if (evt.type === 'blur') {
        evt.target.value = MAX_PASSWORD_LENGTH.toString();

        setPasswordLength(MAX_PASSWORD_LENGTH);

        return;
      }
    } else {
      setPasswordLength(value);
    }
  };

  const onPasswordLengthRangeChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPasswordLength(Number(evt.target.value));
  };

  const onOptionChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    if (passwordOptions.includes(evt.target.value)) {
      if (passwordOptions.length <= 1) return;

      setPasswordOptions(
        passwordOptions.filter((item) => item !== evt.target.value),
      );
    } else {
      setPasswordOptions([...passwordOptions, evt.target.value]);
    }
  };

  // Effects.
  React.useEffect(() => {
    setPassword(
      generatePassword(STARTING_PASSWORD_LENGTH, STARTING_PASSWORD_OPTIONS),
    );
  }, []);

  React.useEffect(() => {
    setPassword(generatePassword(passwordLength, passwordOptions));

    if (passwordLengthRef.current) {
      passwordLengthRef.current.value = passwordLength.toString();
    }
  }, [passwordLength, passwordOptions]);

  return (
    <div className={scss.wrapper}>
      <div className={scss.pg_wrapper}>
        <div className={scss.pg_password}>
          <input
            id="password"
            className={scss.pg_password_input}
            type="text"
            name="password"
            value={password}
            readOnly
          />
          <div className={scss.pg_password_actions}>
            <button
              type="button"
              title="Copy password"
              onClick={copyPasswordToClipboard}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16h140.1l67.9 67.9V320c0 8.8-7.2 16-16 16zm-192 48h192c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9l-67.8-67.9c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64v256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64v256c0 35.3 28.7 64 64 64h192c35.3 0 64-28.7 64-64v-32h-48v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16h32v-48H64z" />
              </svg>
            </button>
            <button
              type="button"
              title="Generate password"
              onClick={regeneratePassword}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="M142.9 142.9c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2S334.3 224 344 224h128c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2S461.9 48.1 455 55l-41.6 41.6c-87.6-86.5-228.7-86.2-315.8 1-24.4 24.4-42 53.1-52.8 83.8-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5c7.7-21.8 20.2-42.3 37.8-59.8zM16 312v128c0 9.7 5.8 18.5 14.8 22.2s19.3 1.7 26.2-5.2l41.6-41.6c87.6 86.5 228.7 86.2 315.8-1 24.4-24.4 42.1-53.1 52.9-83.7 5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8-62.2 62.2-162.7 62.5-225.3 1L185 329c6.9-6.9 8.9-17.2 5.2-26.2S177.7 288 168 288H40c-13.3 0-24 10.7-24 24z" />
              </svg>
            </button>
          </div>
          <div className={scss.pg_password_strength_bar}>
            <div
              className={scss.pg_password_strength_bar_step}
              style={{
                width: calculatePasswordStrength(password).percentage,
                backgroundColor: calculatePasswordStrength(password).color,
              }}
            />
          </div>
        </div>
        <div className={scss.pg_settings}>
          <h2 className={scss.pg_settings_headline}>Customize your password</h2>
          <div className={scss.pg_settings_inner}>
            <div className={scss.pg_password_length}>
              <label
                htmlFor="passwordLength"
                className={scss.pg_password_length_label}>
                Password length
              </label>
              <div className={scss.pg_password_length_input_container}>
                <input
                  ref={passwordLengthRef}
                  id="passwordLength"
                  className={scss.pg_password_length_input}
                  type="number"
                  name="passwordLength"
                  step={1}
                  min={MIN_PASSWORD_LENGTH}
                  max={MAX_PASSWORD_LENGTH}
                  onChange={onPasswordLengthChange}
                  onBlur={onPasswordLengthChange}
                />
                <div className="beautiful_range">
                  <input
                    type="range"
                    id="passwordLengthRange"
                    className={`beautiful_range_input ${scss.pg_password_length_range_input}`}
                    name="passwordLengthRange"
                    value={passwordLength}
                    step={1}
                    min={MIN_PASSWORD_LENGTH}
                    max={MAX_PASSWORD_LENGTH}
                    onChange={onPasswordLengthRangeChange}
                  />
                </div>
              </div>
            </div>
            <div>
              <fieldset>
                <legend>Options</legend>
                <div className="beautiful_checkbox">
                  <input
                    id="option-lowercase"
                    className="beautiful_checkbox_input"
                    type="checkbox"
                    name="option"
                    value="lowercase"
                    checked={passwordOptions.includes('lowercase')}
                    onChange={onOptionChange}
                  />{' '}
                  <label htmlFor="option-lowercase">Lowercase</label>
                </div>
                <div className="beautiful_checkbox">
                  <input
                    id="option-uppercase"
                    className="beautiful_checkbox_input"
                    type="checkbox"
                    name="option"
                    value="uppercase"
                    checked={passwordOptions.includes('uppercase')}
                    onChange={onOptionChange}
                  />{' '}
                  <label htmlFor="option-uppercase">Uppercase</label>
                </div>
                <div className="beautiful_checkbox">
                  <input
                    id="option-numbers"
                    className="beautiful_checkbox_input"
                    type="checkbox"
                    name="option"
                    value="numbers"
                    checked={passwordOptions.includes('numbers')}
                    onChange={onOptionChange}
                  />{' '}
                  <label htmlFor="option-numbers">Numbers</label>
                </div>
                <div className="beautiful_checkbox">
                  <input
                    id="option-symbols"
                    className="beautiful_checkbox_input"
                    type="checkbox"
                    name="option"
                    value="symbols"
                    checked={passwordOptions.includes('symbols')}
                    onChange={onOptionChange}
                  />{' '}
                  <label htmlFor="option-symbols">Symbols</label>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div className={scss.pg_actions}>
          <button
            ref={btnCopyRef}
            className={scss.btn_copy}
            type="button"
            title="Copy Password"
            onClick={copyPasswordToClipboard}>
            Copy Password
          </button>
        </div>
      </div>
    </div>
  );
};
